import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  IconButton,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { IoClose, IoDownloadOutline, IoShareOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import { MOBILE_INSTALL_PROMPT_BOTTOM } from "../constants/mobileApp";

const DISMISS_KEY = "payinapo_hide_install_prompt";

function CompactInstallBanner({
  title,
  subtitle,
  shellBg,
  shellBorder,
  titleColor,
  mutedColor,
  closeHoverBg,
  dismissLabel,
  onDismiss,
  children,
}) {
  return (
    <Box
      display={{ base: "block", lg: "none" }}
      position="fixed"
      left={2}
      right={2}
      bottom={MOBILE_INSTALL_PROMPT_BOTTOM}
      zIndex={1150}
      borderRadius="xl"
      border="1px solid"
      borderColor={shellBorder}
      bg={shellBg}
      backdropFilter="blur(16px) saturate(160%)"
      sx={{ WebkitBackdropFilter: "blur(16px) saturate(160%)" }}
      boxShadow="0 6px 20px rgba(0,0,0,0.24)"
      px={2.5}
      py={1.5}
    >
      <Flex align="center" gap={2} minH="32px">
        <Box flex="1" minW={0}>
          <Text fontSize="11px" fontWeight="semibold" color={titleColor} lineHeight="1.25" noOfLines={1}>
            {title}
          </Text>
          {subtitle ? (
            <Text fontSize="10px" color={mutedColor} lineHeight="1.3" mt={0.5} noOfLines={2}>
              {subtitle}
            </Text>
          ) : null}
        </Box>
        <Flex align="center" gap={0.5} flexShrink={0}>
          {children}
          <IconButton
            icon={<IoClose size={13} />}
            aria-label={dismissLabel}
            variant="ghost"
            size="xs"
            h="26px"
            minW="26px"
            borderRadius="md"
            color={titleColor}
            opacity={0.5}
            _hover={{ bg: closeHoverBg, opacity: 1 }}
            onClick={onDismiss}
          />
        </Flex>
      </Flex>
    </Box>
  );
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIosSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent.toLowerCase();
  const isIos = /iphone|ipad|ipod/.test(ua);
  const isSafari = /safari/.test(ua) && !/crios|fxios|edgios|chrome|android/.test(ua);
  return isIos && isSafari;
}

export default function InstallPrompt({ visible }) {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  const shellBg = useColorModeValue("rgba(255,255,255,0.96)", "rgba(18,18,20,0.96)");
  const shellBorder = useColorModeValue("rgba(15, 23, 42, 0.1)", "rgba(255, 255, 255, 0.08)");
  const titleColor = useColorModeValue("gray.900", "white");
  const mutedColor = useColorModeValue("gray.600", "whiteAlpha.700");
  const closeHoverBg = useColorModeValue("blackAlpha.50", "whiteAlpha.150");
  const iosCtaColor = useColorModeValue("brand.700", "brand.200");
  const iosCtaHoverBg = useColorModeValue("brand.50", "rgba(52,211,153,0.12)");
  const drawerBg = useColorModeValue("gray.900", "gray.900");

  const showIosGuide = visible && !dismissed && !isStandalone() && isIosSafari();
  const showAndroidCard = visible && !dismissed && !isStandalone() && !!deferredPrompt;

  useEffect(() => {
    if (!visible || dismissed || isStandalone()) return undefined;

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    return () => window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  }, [visible, dismissed]);

  const handleDismiss = useCallback(() => {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
    onClose();
  }, [onClose]);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {
      /* ignore */
    }
    setDeferredPrompt(null);
    handleDismiss();
  }, [deferredPrompt, handleDismiss]);

  if (!showAndroidCard && !showIosGuide) return null;

  return (
    <>
      {showAndroidCard && (
        <CompactInstallBanner
          title={t("pwa.installTitle")}
          subtitle={t("pwa.installSubtitle")}
          shellBg={shellBg}
          shellBorder={shellBorder}
          titleColor={titleColor}
          mutedColor={mutedColor}
          closeHoverBg={closeHoverBg}
          dismissLabel={t("pwa.dismiss")}
          onDismiss={handleDismiss}
        >
          <Button
            size="xs"
            h="26px"
            px={2}
            fontSize="10px"
            fontWeight="semibold"
            leftIcon={<IoDownloadOutline size={12} />}
            bgGradient="linear(to-r, brand.300, brand.500)"
            color="gray.900"
            _hover={{ bgGradient: "linear(to-r, brand.400, brand.600)" }}
            onClick={handleInstall}
          >
            {t("pwa.installCta")}
          </Button>
        </CompactInstallBanner>
      )}

      {showIosGuide && (
        <>
          <CompactInstallBanner
            title={t("pwa.installTitle")}
            subtitle={t("pwa.iosHint")}
            shellBg={shellBg}
            shellBorder={shellBorder}
            titleColor={titleColor}
            mutedColor={mutedColor}
            closeHoverBg={closeHoverBg}
            dismissLabel={t("pwa.dismiss")}
            onDismiss={handleDismiss}
          >
            <Button
              size="xs"
              h="26px"
              px={2}
              fontSize="10px"
              fontWeight="semibold"
              variant="outline"
              borderColor="brand.400"
              color={iosCtaColor}
              _hover={{ bg: iosCtaHoverBg }}
              onClick={onOpen}
            >
              {t("pwa.howToInstall")}
            </Button>
          </CompactInstallBanner>

          <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
            <DrawerOverlay bg="blackAlpha.700" backdropFilter="blur(6px)" />
            <DrawerContent
              bg={drawerBg}
              borderTopRadius="2xl"
              borderTopWidth="1px"
              borderColor="whiteAlpha.200"
              pb="calc(16px + env(safe-area-inset-bottom, 0px))"
            >
              <DrawerCloseButton color="white" />
              <DrawerBody pt={10} px={5}>
                <Text fontSize="xl" fontWeight="bold" color="white" textAlign="center" mb={2}>
                  {t("pwa.iosDrawerTitle")}
                </Text>
                <Text fontSize="sm" color="whiteAlpha.700" textAlign="center" mb={6}>
                  {t("pwa.iosDrawerSubtitle")}
                </Text>
                <Flex direction="column" gap={4}>
                  <Flex align="flex-start" gap={3}>
                    <Flex
                      align="center"
                      justify="center"
                      w={8}
                      h={8}
                      borderRadius="full"
                      bg="whiteAlpha.150"
                      flexShrink={0}
                    >
                      <IoShareOutline color="white" size={18} />
                    </Flex>
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" color="white">
                        {t("pwa.iosStep1Title")}
                      </Text>
                      <Text fontSize="sm" color="whiteAlpha.700" mt={0.5}>
                        {t("pwa.iosStep1Body")}
                      </Text>
                    </Box>
                  </Flex>
                  <Flex align="flex-start" gap={3}>
                    <Flex
                      align="center"
                      justify="center"
                      w={8}
                      h={8}
                      borderRadius="full"
                      bg="whiteAlpha.150"
                      flexShrink={0}
                      fontSize="sm"
                      fontWeight="bold"
                      color="white"
                    >
                      +
                    </Flex>
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" color="white">
                        {t("pwa.iosStep2Title")}
                      </Text>
                      <Text fontSize="sm" color="whiteAlpha.700" mt={0.5}>
                        {t("pwa.iosStep2Body")}
                      </Text>
                    </Box>
                  </Flex>
                </Flex>
                <Button
                  mt={8}
                  w="full"
                  h="44px"
                  bgGradient="linear(to-r, brand.300, brand.500)"
                  color="gray.900"
                  _hover={{ bgGradient: "linear(to-r, brand.400, brand.600)" }}
                  onClick={onClose}
                >
                  {t("pwa.gotIt")}
                </Button>
              </DrawerBody>
            </DrawerContent>
          </Drawer>
        </>
      )}
    </>
  );
}
